import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCellSignalSlashThin } from 'react-icons/pi';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { SaveGPSLostDialog } from '../../GpsLost/SaveGPSLostDialog';

import { useLocalization } from '@ais-ui/locale-management';
import { GpsLostConfigurationList } from '../../GpsLost/GPSLostConfigurationList';
import {
  useAllGridJobConfigs,
  useCreateGPSLostJobConfig,
  useGridJobConfigs,
} from '@ais-ui/api';
import { useAuth } from '@ais-ui/auth';

interface GPSLostButtonProps {
  onClick: () => void;
}

const GPSLostButton = ({ onClick }: GPSLostButtonProps) => {
  const permissions = usePermissions();

  const handleClick = (e: React.MouseEvent) => {
    if (permissions.gpsLost) {
      onClick();
    }
  };

  const button = (
    <Button
      id="gps-lost-button"
      size="icon"
      variant="outline"
      className="h-10 w-10 flex-shrink-0 flex-grow-0 relative"
      disabled={!permissions.gpsLost}
      onClick={handleClick}
      aria-disabled={!permissions.gpsLost}
    >
      <PiCellSignalSlashThin className="h-6 w-6" />
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-block">{button}</span>
      </TooltipTrigger>
      <TooltipContent>
        {permissions.gpsLost
          ? 'Manage GPS Lost'
          : 'You do not have permissions to GPS Lost'}
      </TooltipContent>
    </Tooltip>
  );
};

interface GpsLostSearchProps {
  onChange: (value: string) => void;
}

const GpsLostSearch = React.memo(({ onChange }: GpsLostSearchProps) => {
  return (
    <CommandInput
      placeholder="Find GPS lost configurations..."
      onValueChange={onChange}
    />
  );
});

export const GPSLost = () => {
  const [open, setOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const permissions = usePermissions();
  const { credentials } = useAuth();

  const { mutate } = useCreateGPSLostJobConfig();
  const { data, isLoading, isError } = useAllGridJobConfigs({
    enabled: permissions.gpsLost && !!credentials,
    pageSize: 50,
  });
  const { messages } = useLocalization();

  const handlePopoverToggle = useCallback(
    (isOpen: boolean) => {
      // Only allow opening the popover if user has permissions
      if (!permissions.gpsLost && isOpen) {
        return;
      }
      setOpen(isOpen);
    },
    [permissions.gpsLost]
  );

  const handleOpenDialog = useCallback(() => {
    setShowDialog(true);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  // Memoize configurations and filter by search query
  const gpsLostConfigurations = useMemo(() => {
    if (!data?.items) {
      return [];
    }

    if (!searchQuery.trim()) {
      return data.items;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    return data.items.filter((config: any) =>
      config.job_name.toLowerCase().includes(lowerCaseQuery)
    );
  }, [data, searchQuery]);

  return (
    <Popover open={open} onOpenChange={handlePopoverToggle}>
      <PopoverTrigger asChild>
        <span>
          <GPSLostButton onClick={() => setOpen(true)} />
        </span>
      </PopoverTrigger>

      <PopoverContent className="w-96 space-y-4">
        <Command>
          <GpsLostSearch onChange={handleSearchChange} />

          <ScrollArea className="h-[264px] flex-grow p-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center p-4">
                <LoadingSpinner size="small" />
              </div>
            ) : isError ? (
              <div className="text-red-500 text-sm p-4">
                Failed to load GPS Lost configurations. Please try again later.
              </div>
            ) : (
              <>
                <CommandGroup heading="GPS Lost configurations">
                  {gpsLostConfigurations.length === 0 ? (
                    <CommandEmpty>
                      No GPS Lost configurations found.
                    </CommandEmpty>
                  ) : (
                    <GpsLostConfigurationList
                      gpsLostConfigurations={gpsLostConfigurations}
                    />
                  )}
                </CommandGroup>
              </>
            )}
          </ScrollArea>
        </Command>

        <Separator />

        <Button
          id="create-gps-lost-config-button"
          className="w-full"
          onClick={handleOpenDialog}
        >
          Create GPS Lost configuration
        </Button>

        {showDialog && (
          <SaveGPSLostDialog
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            action={'create'}
            mutate={mutate}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
